/**
 * Metadata Builder Component
 * Complete metadata creation with templates, attributes, and preview
 */
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Typography,
  Stack,
  Chip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Divider,
  Alert,
  Collapse,
} from '@mui/material';
import {
  Add,
  Delete,
  ContentCopy,
  Preview,
  ExpandMore,
  ExpandLess,
  CheckCircle,
} from '@mui/icons-material';
import ImageUploader from './ImageUploader';
import ipfsUploadService from '../services/ipfsUpload.service';

interface Attribute {
  trait_type: string;
  value: string | number;
  display_type?: 'string' | 'number' | 'boost_percentage' | 'boost_number' | 'date';
}

interface TokenMetadata {
  name: string;
  description: string;
  image: string;
  external_url?: string;
  attributes: Attribute[];
}

const METADATA_TEMPLATES = {
  blank: {
    name: 'Blank Template',
    metadata: {
      name: '',
      description: '',
      image: '',
      attributes: [],
    },
  },
  roleToken: {
    name: 'Role Token',
    metadata: {
      name: 'Role Token: [Role Name]',
      description: 'Grants specific role permissions',
      image: '',
      attributes: [
        { trait_type: 'Role', value: '' },
        { trait_type: 'Scope', value: 'Global' },
        { trait_type: 'Type', value: 'Administrative' },
      ],
    },
  },
  legalDocument: {
    name: 'Legal Document',
    metadata: {
      name: 'Legal Document',
      description: 'Official legal documentation',
      image: '',
      attributes: [
        { trait_type: 'Document Type', value: '' },
        { trait_type: 'Case ID', value: '' },
        { trait_type: 'Date Filed', value: '', display_type: 'date' as const },
      ],
    },
  },
  nftArtwork: {
    name: 'NFT Artwork',
    metadata: {
      name: 'Digital Artwork',
      description: '',
      image: '',
      attributes: [
        { trait_type: 'Artist', value: '' },
        { trait_type: 'Medium', value: 'Digital' },
        { trait_type: 'Year', value: new Date().getFullYear() },
      ],
    },
  },
};

interface MetadataBuilderProps {
  onComplete: (metadata: TokenMetadata, ipfsUri: string) => void;
  initialMetadata?: Partial<TokenMetadata>;
}

export const MetadataBuilder: React.FC<MetadataBuilderProps> = ({
  onComplete,
  initialMetadata,
}) => {
  const [metadata, setMetadata] = useState<TokenMetadata>({
    name: initialMetadata?.name || '',
    description: initialMetadata?.description || '',
    image: initialMetadata?.image || '',
    external_url: initialMetadata?.external_url || '',
    attributes: initialMetadata?.attributes || [],
  });

  const [uploading, setUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadedUri, setUploadedUri] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleImageUpload = (cid: string, ipfsUri: string, gatewayUrl: string) => {
    setMetadata((prev) => ({ ...prev, image: ipfsUri }));
  };

  const handleTemplateSelect = (templateKey: keyof typeof METADATA_TEMPLATES) => {
    const template = METADATA_TEMPLATES[templateKey];
    setMetadata(template.metadata);
  };

  const handleAddAttribute = () => {
    setMetadata((prev) => ({
      ...prev,
      attributes: [
        ...prev.attributes,
        { trait_type: '', value: '', display_type: 'string' },
      ],
    }));
  };

  const handleUpdateAttribute = (index: number, field: keyof Attribute, value: any) => {
    setMetadata((prev) => {
      const newAttributes = [...prev.attributes];
      newAttributes[index] = { ...newAttributes[index], [field]: value };
      return { ...prev, attributes: newAttributes };
    });
  };

  const handleDeleteAttribute = (index: number) => {
    setMetadata((prev) => ({
      ...prev,
      attributes: prev.attributes.filter((_, i) => i !== index),
    }));
  };

  const handleUploadMetadata = async () => {
    setError(null);

    // Validation
    if (!metadata.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!metadata.image.trim()) {
      setError('Image is required');
      return;
    }

    setUploading(true);

    try {
      // Clean metadata
      const cleanMetadata = {
        ...metadata,
        external_url: metadata.external_url || undefined,
        attributes: metadata.attributes.filter((attr) => attr.trait_type.trim() !== ''),
      };

      const result = await ipfsUploadService.uploadMetadata(cleanMetadata);

      if (result.success && result.ipfsUri) {
        setUploadedUri(result.ipfsUri);
        setUploadComplete(true);
        onComplete(cleanMetadata, result.ipfsUri);
      } else {
        setError('Upload succeeded but URI not available. Try again.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to upload metadata');
    } finally {
      setUploading(false);
    }
  };

  const handleCopyUri = () => {
    if (uploadedUri) {
      navigator.clipboard.writeText(uploadedUri);
    }
  };

  return (
    <Box>
      {!uploadComplete ? (
        <Grid container spacing={3}>
          {/* Left Column - Form */}
          <Grid item xs={12} lg={8}>
            <Stack spacing={3}>
              {/* Template Selection */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Choose Template
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                    {Object.entries(METADATA_TEMPLATES).map(([key, template]) => (
                      <Chip
                        key={key}
                        label={template.name}
                        onClick={() => handleTemplateSelect(key as keyof typeof METADATA_TEMPLATES)}
                        color="primary"
                        variant="outlined"
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Stack>
                </CardContent>
              </Card>

              {/* Image Upload */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Upload Image *
                  </Typography>
                  <ImageUploader onUploadComplete={handleImageUpload} />
                  {metadata.image && (
                    <Alert severity="success" icon={<CheckCircle />} sx={{ mt: 2 }}>
                      Image URI: <code>{metadata.image}</code>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Basic Information */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Basic Information
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      label="Name *"
                      value={metadata.name}
                      onChange={(e) => setMetadata({ ...metadata, name: e.target.value })}
                      fullWidth
                      placeholder="e.g., Role Token: Minter"
                    />
                    <TextField
                      label="Description"
                      value={metadata.description}
                      onChange={(e) => setMetadata({ ...metadata, description: e.target.value })}
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Describe this token..."
                    />
                    <TextField
                      label="External URL (optional)"
                      value={metadata.external_url}
                      onChange={(e) => setMetadata({ ...metadata, external_url: e.target.value })}
                      fullWidth
                      placeholder="https://example.com/token/123"
                    />
                  </Stack>
                </CardContent>
              </Card>

              {/* Attributes */}
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">Attributes</Typography>
                    <Button
                      startIcon={<Add />}
                      onClick={handleAddAttribute}
                      variant="outlined"
                      size="small"
                    >
                      Add Attribute
                    </Button>
                  </Box>

                  <Stack spacing={2}>
                    {metadata.attributes.map((attr, index) => (
                      <Paper key={index} sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={4}>
                            <TextField
                              label="Trait Type"
                              value={attr.trait_type}
                              onChange={(e) =>
                                handleUpdateAttribute(index, 'trait_type', e.target.value)
                              }
                              fullWidth
                              size="small"
                              placeholder="e.g., Role"
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              label="Value"
                              value={attr.value}
                              onChange={(e) => handleUpdateAttribute(index, 'value', e.target.value)}
                              fullWidth
                              size="small"
                              placeholder="e.g., Minter"
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Display Type</InputLabel>
                              <Select
                                value={attr.display_type || 'string'}
                                onChange={(e) =>
                                  handleUpdateAttribute(index, 'display_type', e.target.value)
                                }
                                label="Display Type"
                              >
                                <MenuItem value="string">String</MenuItem>
                                <MenuItem value="number">Number</MenuItem>
                                <MenuItem value="date">Date</MenuItem>
                                <MenuItem value="boost_percentage">Boost %</MenuItem>
                                <MenuItem value="boost_number">Boost #</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={1}>
                            <IconButton
                              onClick={() => handleDeleteAttribute(index)}
                              color="error"
                              size="small"
                            >
                              <Delete />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}

                    {metadata.attributes.length === 0 && (
                      <Alert severity="info">
                        No attributes added yet. Click "Add Attribute" to get started.
                      </Alert>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              {/* Error Display */}
              {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {/* Action Buttons */}
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleUploadMetadata}
                  disabled={uploading || !metadata.name || !metadata.image}
                  fullWidth
                >
                  {uploading ? 'Uploading to IPFS...' : 'Upload Metadata to IPFS'}
                </Button>
              </Stack>
            </Stack>
          </Grid>

          {/* Right Column - Preview */}
          <Grid item xs={12} lg={4}>
            <Card sx={{ position: 'sticky', top: 20 }}>
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setShowPreview(!showPreview)}
                >
                  <Typography variant="h6">
                    <Preview sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Preview
                  </Typography>
                  {showPreview ? <ExpandLess /> : <ExpandMore />}
                </Box>

                <Collapse in={showPreview}>
                  <Divider sx={{ mb: 2 }} />

                  {metadata.image && (
                    <Box
                      component="img"
                      src={ipfsUploadService.getGatewayUrl(metadata.image)}
                      alt="Preview"
                      sx={{
                        width: '100%',
                        borderRadius: 2,
                        mb: 2,
                        border: '1px solid',
                        borderColor: 'grey.300',
                      }}
                    />
                  )}

                  <Typography variant="h6" gutterBottom>
                    {metadata.name || 'Untitled'}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" paragraph>
                    {metadata.description || 'No description'}
                  </Typography>

                  {metadata.external_url && (
                    <Typography variant="caption" display="block" gutterBottom>
                      <strong>External URL:</strong>{' '}
                      <a href={metadata.external_url} target="_blank" rel="noopener noreferrer">
                        {metadata.external_url}
                      </a>
                    </Typography>
                  )}

                  {metadata.attributes.length > 0 && (
                    <Box mt={2}>
                      <Typography variant="subtitle2" gutterBottom>
                        Attributes
                      </Typography>
                      <Stack spacing={1}>
                        {metadata.attributes
                          .filter((attr) => attr.trait_type.trim() !== '')
                          .map((attr, index) => (
                            <Paper key={index} sx={{ p: 1, bgcolor: 'grey.100' }}>
                              <Typography variant="caption" color="text.secondary">
                                {attr.trait_type}
                              </Typography>
                              <Typography variant="body2" fontWeight={500}>
                                {attr.value}
                              </Typography>
                            </Paper>
                          ))}
                      </Stack>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="caption" color="text.secondary">
                    JSON Preview:
                  </Typography>
                  <Paper
                    sx={{
                      p: 1,
                      mt: 1,
                      bgcolor: 'grey.900',
                      color: 'grey.100',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      overflow: 'auto',
                      maxHeight: 200,
                    }}
                  >
                    <pre>
                      {JSON.stringify(
                        {
                          ...metadata,
                          attributes: metadata.attributes.filter(
                            (attr) => attr.trait_type.trim() !== ''
                          ),
                        },
                        null,
                        2
                      )}
                    </pre>
                  </Paper>
                </Collapse>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Card>
          <CardContent>
            <Stack spacing={3} alignItems="center">
              <CheckCircle sx={{ fontSize: 80, color: 'success.main' }} />
              <Typography variant="h4" align="center" color="success.main">
                Metadata Uploaded Successfully!
              </Typography>
              <Paper sx={{ p: 3, bgcolor: 'grey.50', width: '100%', maxWidth: 600 }}>
                <Typography variant="subtitle2" gutterBottom>
                  IPFS URI:
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <TextField
                    value={uploadedUri}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                      sx: { fontFamily: 'monospace', fontSize: '0.9rem' },
                    }}
                  />
                  <IconButton onClick={handleCopyUri} color="primary">
                    <ContentCopy />
                  </IconButton>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                  Use this URI when configuring your token
                </Typography>
              </Paper>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default MetadataBuilder;
